import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { skipToken } from '@reduxjs/toolkit/query';
import { DATA_SETS_ENUM_TYPES } from 'constants/dataSets';
import { NET_BOOK_CURRENCY, VALUE_CHAIN_SECTION } from 'constants/formFields';
import {
  useEnumQuery,
  useGetNaceCodeTreeQuery,
} from 'store/slices/dataSets/apis/dataSetsApi';
import { selectEnums } from 'store/slices/dataSets/selectors';
import { useGetLocationsCountriesQuery } from 'store/slices/geoLocations/apis/geoLocationsApi';
import { useGetBalanceSheetAssetsDataQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';
import getOwnOperationsDefaultValues from 'utils/formHelpers/getOwnOperationsDefaultValues';
import { createOwnOperationsSchema } from 'utils/validators';

import type { TCreateOperationsSchema } from 'utils/validators';
import type ICreateBalanceSheetAssetsForm from '../interfaces/ICreateBalanceSheetAssetsForm';

const useCreateBalanceSheetAssetsForm = ({
  recordId, companyCurrency, valueChainSectionCurrent,
}: ICreateBalanceSheetAssetsForm) => {
  const { data } = useGetBalanceSheetAssetsDataQuery(
    recordId ? { chainId: `${recordId}` } : skipToken,
    { refetchOnMountOrArgChange: true },
  );
  const hasExistingUniqueIdentifier = Boolean(data?.data.uniqueIdentifier?.trim());
  const isUniqueIdentifierEditable = recordId ? Boolean(data?.data) && !hasExistingUniqueIdentifier : true;
  const { data: naceCodeTreeData } = useGetNaceCodeTreeQuery();

  const { data: enumDataRaw } = useEnumQuery({
    types: [DATA_SETS_ENUM_TYPES.currency, DATA_SETS_ENUM_TYPES.ownership, DATA_SETS_ENUM_TYPES.valueChainSection,
      DATA_SETS_ENUM_TYPES.businessUnitType, DATA_SETS_ENUM_TYPES.assetCategory, DATA_SETS_ENUM_TYPES.ownedLeased],
  });

  const enumData = useMemo(() => selectEnums(enumDataRaw), [enumDataRaw]);

  const isFormInitialized = useRef(false);

  const { data: locationsCountries } = useGetLocationsCountriesQuery();

  const {
    control,
    handleSubmit,
    formState,
    formState: { isDirty, isLoading },
    reset,
    setError,
    getValues,
    watch,
    setValue,
    resetField,
  } = useForm<TCreateOperationsSchema>({
    mode: 'onSubmit',
    resolver: yupResolver(createOwnOperationsSchema),
    defaultValues: getOwnOperationsDefaultValues(
      {
        values: data?.data,
        naceCodeTreeData,
        companyCurrency,
        enumData,
        locationsCountries: locationsCountries?.data || [],
        valueChainSectionCurrent,
      },
    ),
  });

  useEffect(() => {
    if (companyCurrency) {
      setValue(NET_BOOK_CURRENCY, companyCurrency);
    }
  }, [companyCurrency, setValue]);

  useEffect(() => {
    if (data?.data && naceCodeTreeData && enumData && !isFormInitialized.current) {
      reset(getOwnOperationsDefaultValues({
        values: data?.data,
        naceCodeTreeData,
        companyCurrency,
        enumData,
        locationsCountries: locationsCountries?.data || [],
        valueChainSectionCurrent,
      }));
      isFormInitialized.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data, naceCodeTreeData, enumData, companyCurrency, locationsCountries]);

  useEffect(() => {
    if (valueChainSectionCurrent) {
      setValue(VALUE_CHAIN_SECTION, {
        value: valueChainSectionCurrent,
        label: valueChainSectionCurrent,
      });
    }
  }, [valueChainSectionCurrent, setValue]);

  useEffect(() => {
    isFormInitialized.current = false;
  }, [recordId]);

  const fieldsName = Object.keys(getValues());

  return {
    control,
    handleSubmit,
    isDirty,
    reset,
    isLoading,
    setError,
    fieldsName,
    watch,
    setValue,
    resetField,
    formState,
    isUniqueIdentifierEditable,
  };
};

export default useCreateBalanceSheetAssetsForm;
